import React, { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { 
  Loader2, RefreshCw, Download, AlertCircle, Check, Upload, ChevronDown, Calendar, Trash2, FileText
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { DayPicker } from "react-day-picker";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { format, subMonths, startOfMonth, endOfMonth, subDays } from "date-fns";
import { useAuth } from "@/hooks/use-auth";
import { fetchExportHistory, downloadExport, deleteExport, createExportJob, type ExportHistoryItem, type ExportHistoryResponse } from "@/data-access/exports";
import { pollExportJob } from "@/lib/poll-export-job";
import { handleUsageLimitError, useUsageLimitStore } from "@/stores/usageLimitStore";
import { useQuery } from "@tanstack/react-query";
import { fetchUserUsageStats } from "@/data-access/users";

interface BatchImportProps {
  onAddressesProcessed?: (result: any) => void;
}

export default function BatchAddressImport({ onAddressesProcessed }: BatchImportProps) {
  const auth = useAuth();
  
  // Get usage stats for current user
  const { data: usageStats } = useQuery({
    queryKey: ['/api/users/usage-stats'],
    queryFn: fetchUserUsageStats,
    enabled: !!auth.user, // Only fetch if user is authenticated
  });

  const { showModal } = useUsageLimitStore();
  
  const [file, setFile] = useState<File | null>(null);
  const [addresses, setAddresses] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showAddressList, setShowAddressList] = useState(false);
  const [invalidAddresses, setInvalidAddresses] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ completed: number; total: number } | null>(null);
  const [showFormatExample, setShowFormatExample] = useState(false);
  const [fileFormat, setFileFormat] = useState<"txt" | "csv" | "json">("txt");
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [processingResult, setProcessingResult] = useState<any>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  
  // Export history state
  const [exportHistory, setExportHistory] = useState<ExportHistoryResponse | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
  const [selectedExportAddresses, setSelectedExportAddresses] = useState<string[]>([]);
  const [isAddressPopupOpen, setIsAddressPopupOpen] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const exportCancelledRef = useRef(false);
  const { toast } = useToast();

  // Cleanup polling on unmount
  React.useEffect(() => {
    return () => { exportCancelledRef.current = true; };
  }, []);

  // Default the date range to the previous calendar month
  const today = new Date();
  const defaultStartDate = startOfMonth(subMonths(today, 1));
  const defaultEndDate = endOfMonth(subMonths(today, 1));

  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate);
  const [startDateMonth, setStartDateMonth] = useState<Date | undefined>(defaultStartDate);
  const [endDateMonth, setEndDateMonth] = useState<Date | undefined>(defaultEndDate);

  // Function to validate date range (max 365 days)
  const validateDateRange = (start: Date | null, end: Date | null) => {
    if (!start || !end) {
      setDateRangeError(null);
      return true;
    }

    const diffInMs = end.getTime() - start.getTime();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays > 365) {
      setDateRangeError(`Date range is ${diffInDays} days. Maximum allowed is 365 days (1 year).`);
      return false;
    } else if (diffInDays < 0) {
      setDateRangeError("End date must be after start date.");
      return false;
    } else {
      setDateRangeError(null);
      return true;
    }
  };

  // Update setStartDate to include validation
  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    validateDateRange(date, endDate);
    if (date) {
      setStartDateMonth(date);
    }
  };

  // Update setEndDate to include validation
  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
    validateDateRange(startDate, date);
    if (date) {
      setEndDateMonth(date);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setValidationError(null);
    setAddresses([]);
    setInvalidAddresses([]);
    setShowAddressList(false);
    
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    // Store the filename without extension for export naming
    const fileNameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, "");
    setUploadedFileName(fileNameWithoutExt);
    setProcessingStatus("Reading file...");
    setIsProcessing(true);
    
    try {
      const text = await readFileAsText(selectedFile);
      // Fix: Handle both Windows and Unix line endings
      const lines = text.split(/\r?\n/).filter(line => line.trim() !== "");
      
      // Check if file exceeds 100 lines
      if (lines.length > 100) {
        setValidationError(`File contains ${lines.length} lines. Maximum allowed is 100 lines.`);
        setIsProcessing(false);
        setProcessingStatus("");
        return;
      }
      
      console.log("Found lines in file:", lines);
      
      // Validate each line as a Bitcoin address
      const validAddresses: string[] = [];
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        validAddresses.push(trimmedLine);
      }
      
      setAddresses(validAddresses);
      
      if (validAddresses.length === 0) {
        setValidationError("No valid Bitcoin addresses found in the file");
        setIsProcessing(false);
        setProcessingStatus("");
      } else {
        setShowAddressList(true);
        
        // Automatically process the addresses
        setProcessingStatus(`Processing ${validAddresses.length} addresses...`);
        
        try {
          const response = await apiRequest("POST", "/api/blockchain/batch-addresses", {
            addresses: validAddresses,
            startDate: startDate ? format(startDate, "yyyy-MM-dd") : null,
            endDate: endDate ? format(endDate, "yyyy-MM-dd") : null
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Failed to process addresses");
          }
          
          const result = await response.json();
          
          toast({
            title: "Addresses processed successfully",
            description: `All ${result.totalAddresses} addresses loaded for export`,
          });
          
          if (onAddressesProcessed) {
            onAddressesProcessed(result);
          }
          
          setProcessingResult(result);
          
        } catch (error) {
          console.error("Error processing addresses:", error);
          toast({
            title: "Processing failed",
            description: error instanceof Error ? error.message : "Failed to process addresses",
            variant: "destructive"
          });
        } finally {
          setIsProcessing(false);
          setProcessingStatus("");
        }
      }
      
    } catch (error) {
      console.error("Error reading file:", error);
      setValidationError("Failed to read the file");
      setIsProcessing(false);
      setProcessingStatus("");
    }
  };
  
  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(event.target.result as string);
        } else {
          reject(new Error("Failed to read file"));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  };
  
  const handleReset = () => {
    setFile(null);
    setAddresses([]);
    setInvalidAddresses([]);
    setShowAddressList(false);
    setValidationError(null);
    setDateRangeError(null);
    setProcessingResult(null);
    setUploadedFileName("");
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setStartDateMonth(defaultStartDate);
    setEndDateMonth(defaultEndDate);
    validateDateRange(defaultStartDate, defaultEndDate);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  
  const handleExportCSV = async () => {
    // Check usage limit before proceeding
    if (usageStats && usageStats.usage.batchExports >= 30 && !usageStats.isAdmin) {
      showModal({
        usageType: 'batch_exports',
        current: usageStats.usage.batchExports,
        limit: 30,
        resetDate: usageStats.resetDate,
      });
      return;
    }

    // Get only successfully processed addresses if we have processing results
    let validAddressesToExport = addresses;

    if (processingResult && processingResult.results) {
      validAddressesToExport = processingResult.results
        .filter((result: any) => result.status === "success")
        .map((result: any) => result.address);
    }

    if (validAddressesToExport.length === 0) {
      setValidationError("No valid addresses to export");
      return;
    }

    setIsExporting(true);
    setExportProgress({ completed: 0, total: validAddressesToExport.length });

    try {
      const { jobId } = await createExportJob({
        addresses: validAddressesToExport,
        startDate: startDate ? format(startDate, "yyyy-MM-dd") : null,
        endDate: endDate ? format(endDate, "yyyy-MM-dd") : null,
        fileName: uploadedFileName || null,
        source: "batch_exports",
      });

      exportCancelledRef.current = false;
      await pollExportJob(jobId, {
        onProgress: (p) => setExportProgress(p),
        onCompleted: () => {
          toast({
            title: "Export successful",
            description: `Report for ${validAddressesToExport.length} addresses downloaded as ZIP file`,
          });
          loadExportHistory(1);
          setIsExporting(false);
          setExportProgress(null);
        },
        onFailed: (msg) => {
          toast({ title: "Export failed", description: msg, variant: "destructive" });
          setIsExporting(false);
          setExportProgress(null);
        },
        onTimeout: () => {
          toast({
            title: "Export timed out",
            description: "The export is taking longer than expected. Check your export history for the result.",
            variant: "destructive",
          });
          setIsExporting(false);
          setExportProgress(null);
        },
        isCancelled: () => exportCancelledRef.current,
      });
    } catch (error: any) {
      console.error("Error exporting addresses:", error);

      // Handle usage limit error (429)
      if (error.status === 429 && error.data?.usageType) {
        handleUsageLimitError(error.data);
      } else if (error.status === 409) {
        toast({
          title: "Export in progress",
          description: "You already have an export in progress. Please wait for it to complete.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Export failed",
          description: error.message || "Something went wrong with the CSV export. Please try again later.",
          variant: "destructive",
        });
      }
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  // Export history functions
  const loadExportHistory = async (page = 1) => {
    setIsLoadingHistory(true);
    try {
      const history = await fetchExportHistory(page, 10);
      setExportHistory(history);
      setCurrentHistoryPage(page);
    } catch (error) {
      toast({
        title: "Failed to load export history",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive"
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleDownloadExport = async (exportId: number) => {
    try {
      await downloadExport(exportId);
      toast({
        title: "Download started",
        description: "The export file download has started",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: error instanceof Error ? error.message : "Failed to download export",
        variant: "destructive"
      });
    }
  };

  const handleDeleteExport = async (exportId: number) => {
    if (!confirm("Are you sure you want to delete this export? This action cannot be undone.")) {
      return;
    }

    try {
      await deleteExport(exportId);
      toast({
        title: "Export deleted",
        description: "The export has been deleted successfully",
      });
      // Reload the current page
      loadExportHistory(currentHistoryPage);
    } catch (error) {
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete export",
        variant: "destructive"
      });
    }
  };

  // Load export history on component mount
  React.useEffect(() => {
    loadExportHistory();
  }, []);
  
  // Add drag and drop capabilities
  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Simulate the file input change event
      const event = {
        target: {
          files
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      handleFileChange(event);
    }
  }, []);

  const handleFileUploadClick = () => {
    // Trigger hidden file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const toggleFormatExample = () => {
    setShowFormatExample(!showFormatExample);
  };

  return (
    <div className="w-full space-y-6 p-8 bg-card border border-border rounded-xl shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 px-6 py-5 rounded-lg mb-6">
        <Upload className="h-5 w-5 text-blue-500 dark:text-blue-400" />
        <div>
          <h2 className="text-lg font-semibold dark:text-white">Batch Address Import</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Upload and analyze multiple blockchain addresses simultaneously
          </p>
        </div>
      </div>
      
      {/* Main content wrapped with padding */}
      <div className="p-6">
        {/* Format Tabs */}
        {/* <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-6 max-w-[300px] mx-auto">
          <Tabs defaultValue="txt" onValueChange={(value) => setFileFormat(value as "txt" | "csv" | "json")}>
            <TabsList className="grid grid-cols-3 w-full bg-transparent gap-1">
              <TabsTrigger value="txt" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 data-[state=active]:shadow-sm px-8 py-1.5 rounded-md">TXT</TabsTrigger>
              <TabsTrigger value="csv" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 data-[state=active]:shadow-sm px-8 py-1.5 rounded-md">CSV</TabsTrigger>
              <TabsTrigger value="json" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 data-[state=active]:shadow-sm px-8 py-1.5 rounded-md">JSON</TabsTrigger>
            </TabsList>
          </Tabs>
        </div> */}
        
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* File Upload Area */}
          <div>
            <p className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">Upload Address List (TXT)</p>
            <div 
              className="border border-dashed border-gray-300 dark:border-gray-600 rounded-md h-[140px] flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-all dark:bg-gray-800/50"
              onClick={handleFileUploadClick}
              onDragOver={onDragOver}
              onDrop={onDrop}
            >
              <Input
                id="file-upload"
                type="file"
                accept=".txt"
                ref={fileInputRef}
                onChange={handleFileChange}
                disabled={isProcessing || isExporting}
                className="hidden"
              />
              
              <Upload className="h-6 w-6 text-gray-500 dark:text-gray-400 mb-3" />
              <p className="text-center font-medium text-gray-700 dark:text-gray-200">
                Choose TXT File
              </p>
              <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                Drag & drop or click to browse
              </p>
            </div>
            
            {/* View Format Example */}
            <div className="mt-4">
              <button 
                onClick={toggleFormatExample}
                className="flex items-center text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <code className="text-xs mr-1.5">&lt;/&gt;</code>
                View Format Example
                <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${showFormatExample ? 'rotate-180' : ''}`} />
              </button>
              
              {showFormatExample && (
                <div className="mt-2 text-xs bg-gray-50 dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700">
                  <p className="font-mono mb-1 dark:text-gray-300">1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa</p>
                  <p className="font-mono mb-1 dark:text-gray-300">3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy</p>
                  <p className="font-mono dark:text-gray-300">bc1qw508d6qejxtdg4y5r3zarvary0c5xw7kv8f3t4</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Date Range Filters */}
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">Start Date</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      disabled={isProcessing || isExporting}
                      className="relative w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 h-12 pr-12 rounded-lg text-left focus-visible:ring-primary focus-visible:ring-1 focus-visible:border-primary text-gray-900 dark:text-white flex items-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className={cn('pl-3', {
                        "text-gray-500 dark:text-gray-400": !startDate,
                      })}>
                        {startDate ? format(startDate, "yyyy/MM/dd") : "Pick a date"}
                      </span>
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 pointer-events-none">
                        <Calendar className="h-4 w-4" />
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="p-2 w-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md">
                    <DayPicker
                      mode="single"
                      captionLayout="dropdown"
                      selected={startDate ?? undefined}
                      month={startDateMonth ?? startDate ?? undefined}
                      onMonthChange={setStartDateMonth}
                      onSelect={(date) => handleStartDateChange(date ?? null)}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">End Date</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    className="h-5 p-2 mb-1"
                    onClick={() => {
                      const today = new Date();
                      handleEndDateChange(today);
                      setEndDateMonth(today); // Update calendar to show current month
                    }}
                    disabled={isProcessing || isExporting}
                  >
                    Today
                  </Button>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      disabled={isProcessing || isExporting}
                      className="relative w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 h-12 pr-12 rounded-lg text-left focus-visible:ring-primary focus-visible:ring-1 focus-visible:border-primary text-gray-900 dark:text-white flex items-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className={cn('pl-3', {
                        "text-gray-500 dark:text-gray-400": !endDate,
                      })}>
                        {endDate ? format(endDate, "yyyy/MM/dd") : "Pick a date"}
                      </span>
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 pointer-events-none">
                        <Calendar className="h-4 w-4" />
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="p-2 w-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md">
                    <DayPicker
                      mode="single"
                      captionLayout="dropdown"
                      selected={endDate ?? undefined}
                      month={endDateMonth ?? endDate ?? undefined}
                      onMonthChange={setEndDateMonth}
                      onSelect={(date) => handleEndDateChange(date ?? null)}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Quick Date Range Selection */}
            <div className="mt-2">
              <RadioGroup
                defaultValue="last-month"
                onValueChange={(value) => {
                  const today = new Date();
                  let newStartDate, newEndDate;
                  switch (value) {
                    case "last-week":
                      newStartDate = subDays(today, 7);
                      newEndDate = today;
                      break;
                    case "last-month":
                      newStartDate = startOfMonth(subMonths(today, 1));
                      newEndDate = endOfMonth(subMonths(today, 1));
                      break;
                    case "last-3-months":
                      newStartDate = startOfMonth(subMonths(today, 3));
                      newEndDate = endOfMonth(subMonths(today, 1));
                      break;
                    default:
                      return;
                  }
                  setStartDate(newStartDate);
                  setEndDate(newEndDate);
                  setStartDateMonth(newStartDate);
                  setEndDateMonth(newEndDate);
                  validateDateRange(newStartDate, newEndDate);
                }}
                className="flex flex-row gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="last-week" id="last-week" />
                  <label htmlFor="last-week" className="text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                    Last week
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="last-month" id="last-month" />
                  <label htmlFor="last-month" className="text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                    Last month
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="last-3-months" id="last-3-months" />
                  <label htmlFor="last-3-months" className="text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                    Last 3 months
                  </label>
                </div>
              </RadioGroup>
            </div>

            {/* Date Range Error Message */}
            {dateRangeError && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{dateRangeError}</AlertDescription>
              </Alert>
            )}
          </div>
        </div>
        
        {/* Processing indication and errors */}
        {isProcessing && (
          <div className="mb-6 space-y-3 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
              <p className="text-sm font-medium text-blue-700 dark:text-blue-300">{processingStatus}</p>
            </div>
            <Progress value={45} className="h-2" />
          </div>
        )}
        
        {validationError && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{validationError}</AlertDescription>
          </Alert>
        )}
        
        {/* Features and Information Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Features */}
          <div>
            <div className="flex items-center mb-3">
              <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full p-1.5 mr-2">
                <Check className="h-4 w-4 text-blue-500 dark:text-blue-400" />
              </div>
              <span className="font-medium dark:text-white">Features</span>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-start">
                <Check className="h-4 w-4 text-green-500 dark:text-green-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium dark:text-white">Simple Format:</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Upload a plain text file with one Bitcoin address per line</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Check className="h-4 w-4 text-green-500 dark:text-green-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium dark:text-white">Fast Processing:</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Our system efficiently processes thousands of addresses</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Check className="h-4 w-4 text-green-500 dark:text-green-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium dark:text-white">Date Filtering:</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Optionally filter results by transaction dates</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Check className="h-4 w-4 text-green-500 dark:text-green-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium dark:text-white">Export Results:</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Download comprehensive CSV reports for further analysis</p>
                </div>
              </div>
            </div>
          </div>
          
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={isProcessing || isExporting}
            size="sm"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reset
          </Button>
          
          <div className="flex gap-3">
            <Button
              variant="default"
              onClick={handleExportCSV}
              disabled={
                (processingResult ? 
                  processingResult.results?.filter((r: any) => r.status === "success").length === 0 : 
                  addresses.length === 0
                ) || isProcessing || isExporting || !!dateRangeError
              }
              size="sm"
            >
              {isExporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {exportProgress && exportProgress.total > 0
                    ? `Exporting... ${exportProgress.completed} of ${exportProgress.total} addresses`
                    : "Starting export..."}
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* File Details (only shown after file upload) */}
        {showAddressList && (
          <div className="mt-6">
            {/* Show processing results from API */}
            {processingResult && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {processingResult.totalAddresses}
                    </div>
                    <div className="text-sm text-blue-700 dark:text-blue-300">Total Addresses</div>
                  </div>
                  
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {processingResult.processedAddresses}
                    </div>
                    <div className="text-sm text-green-700 dark:text-green-300">Valid Addresses</div>
                  </div>
                  
                  <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {processingResult.failedAddresses}
                    </div>
                    <div className="text-sm text-red-700 dark:text-red-300">Invalid Addresses</div>
                  </div>
                </div>

                {/* Successful Addresses */}
                {processingResult.results?.filter((r: any) => r.status === "success").length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-green-600 dark:text-green-400 mb-2">
                      <Check className="h-4 w-4 inline mr-1" />
                      Valid Addresses ({processingResult.results.filter((r: any) => r.status === "success").length})
                    </h3>
                    <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-md max-h-40 overflow-y-auto text-sm font-mono dark:border dark:border-green-800/30">
                      {processingResult.results
                        .filter((r: any) => r.status === "success")
                        .map((result: any, i: number) => (
                          <div key={i} className="mb-1 text-green-700 dark:text-green-300 flex items-center">
                            <Check className="h-3 w-3 mr-2 text-green-500" />
                            {result.address}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Failed Addresses */}
                {processingResult.results?.filter((r: any) => r.status === "error").length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      Invalid Addresses ({processingResult.results.filter((r: any) => r.status === "error").length})
                    </h3>
                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-md max-h-40 overflow-y-auto text-sm font-mono dark:border dark:border-red-800/30">
                      {processingResult.results
                        .filter((r: any) => r.status === "error")
                        .map((result: any, i: number) => (
                          <div key={i} className="mb-2">
                            <div className="text-red-700 dark:text-red-400 flex items-center">
                              <AlertCircle className="h-3 w-3 mr-2 text-red-500" />
                              {result.address}
                            </div>
                            {result.error && (
                              <div className="text-xs text-red-600 dark:text-red-300 ml-5 mt-1">
                                {result.error}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {auth.user?.featureFlags?.includes("export_history_flag") && (
          <>
            {/* Export History Section */}
            <div className="mt-8">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold dark:text-white">Previous Exports</h3>
                <Button
                  onClick={() => loadExportHistory(1)}
                  variant="outline"
                  size="sm"
                  disabled={isLoadingHistory}
                >
                  {isLoadingHistory ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Refresh
                </Button>
              </div>

              {isLoadingHistory && !exportHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span className="text-gray-600 dark:text-gray-400">Loading export history...</span>
                </div>
              ) : exportHistory && exportHistory.exports.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Export Details
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Addresses
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Date Range
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {exportHistory.exports.map((exportItem) => {
                          const addressCount = exportItem.addresses.split(',').length;
                          const addressList = exportItem.addresses.split(',').map(addr => addr.trim());
                          return (
                            <tr key={exportItem.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-4 py-4">
                                <div className="flex items-start">
                                  <FileText className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                                  <div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {exportItem.fileName}.txt
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <Dialog open={isAddressPopupOpen} onOpenChange={setIsAddressPopupOpen}>
                                  <DialogTrigger asChild>
                                    <button
                                      onClick={() => {
                                        setSelectedExportAddresses(addressList);
                                        setIsAddressPopupOpen(true);
                                      }}
                                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium text-sm underline cursor-pointer"
                                    >
                                      {addressCount} address{addressCount !== 1 ? 'es' : ''}
                                    </button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
                                    <DialogHeader>
                                      <DialogTitle>Addresses in Export</DialogTitle>
                                    </DialogHeader>
                                    <div className="mt-4">
                                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                        Total: {selectedExportAddresses.length} address{selectedExportAddresses.length !== 1 ? 'es' : ''}
                                      </div>
                                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 max-h-96 overflow-y-auto">
                                        <div className="space-y-2">
                                          {selectedExportAddresses.map((address, index) => (
                                            <div key={index} className="font-mono text-sm">
                                              {address}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 dark:text-white">
                                  {exportItem.startDate && exportItem.endDate ? (
                                    <>
                                      {format(new Date(`${exportItem.startDate}T00:00:00`), 'MMM d, yyyy')}
                                      <br />
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        to {format(new Date(`${exportItem.endDate}T00:00:00`), 'MMM d, yyyy')}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-gray-500 dark:text-gray-400">All time</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 dark:text-white">
                                  {format(new Date(exportItem.createdAt), 'MMM d, yyyy')}
                                  <br />
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {format(new Date(exportItem.createdAt), 'h:mm a')}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right">
                                <div className="flex items-center justify-end space-x-2">
                                  <Button
                                    onClick={() => handleDownloadExport(exportItem.id)}
                                    variant="outline"
                                    size="sm"
                                    className="h-8"
                                  >
                                    <Download className="h-3 w-3 mr-1" />
                                    Download Report
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteExport(exportItem.id)}
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {exportHistory.pagination.totalPages > 1 && (
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Showing {((exportHistory.pagination.currentPage - 1) * exportHistory.pagination.itemsPerPage) + 1} to{' '}
                          {Math.min(
                            exportHistory.pagination.currentPage * exportHistory.pagination.itemsPerPage,
                            exportHistory.pagination.totalItems
                          )}{' '}
                          of {exportHistory.pagination.totalItems} exports
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => loadExportHistory(exportHistory.pagination.currentPage - 1)}
                            disabled={!exportHistory.pagination.hasPreviousPage || isLoadingHistory}
                            variant="outline"
                            size="sm"
                          >
                            Previous
                          </Button>
                          <Button
                            onClick={() => loadExportHistory(exportHistory.pagination.currentPage + 1)}
                            disabled={!exportHistory.pagination.hasNextPage || isLoadingHistory}
                            variant="outline"
                            size="sm"
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No previous exports found</p>
                  <p className="text-sm">Export some addresses to see them here</p>
                </div>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
}
